import { FulfillmentGroupData } from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import { groupFulfillmentOrderLineItems } from "@redotech/redo-fulfillment-group-common/fulfillment-order-line-item";
import { TeamContext } from "@redotech/redo-web/context/team-context";
import { Flex } from "@redotech/redo-web/flex";
import { toMoney } from "@redotech/redo-web/fx/to-money";
import {
  ItemTableHeader,
  ProductListRow,
} from "@redotech/redo-web/product-list-row";
import { memo, useContext, useMemo } from "react";
import { CurrentInventoryButton } from "../../../common/current-inventory-button";
import { LineItemLots } from "../../../common/line-item-lots";
import { SplitGroupButton } from "../../../common/split-group-button";
import {
  RedoContentCard,
  RedoContentCardReorderProps,
} from "./redo-content-card";

export const ItemsCard = memo(function ItemsCard({
  fulfillmentGroup,
  triggerReload,
  everythingDisabled,
  reorderHandleProps,
  showReorderHandle,
}: {
  fulfillmentGroup: FulfillmentGroupData;
  triggerReload: () => Promise<void>;
  everythingDisabled: boolean;
} & RedoContentCardReorderProps) {
  const lineItemsQuantity = useMemo(() => {
    return fulfillmentGroup.items.reduce((acc, item) => acc + item.quantity, 0);
  }, [fulfillmentGroup]);

  return (
    <RedoContentCard
      contentSpacing="none"
      headerActions={
        <Flex align="center" gap="sm">
          <CurrentInventoryButton fulfillmentGroups={[fulfillmentGroup]} />
          <SplitGroupButton
            disabled={everythingDisabled}
            fulfillmentGroup={fulfillmentGroup}
            isSlideout={false}
            size="sm"
            triggerReload={triggerReload}
          />
        </Flex>
      }
      reorderHandleProps={reorderHandleProps}
      showReorderHandle={showReorderHandle}
      title={`Items in this shipment (${lineItemsQuantity})`}
    >
      <ItemsCardTable fulfillmentGroup={fulfillmentGroup} />
    </RedoContentCard>
  );
});

export const ItemsCardTable = memo(function ItemsCardTable({
  fulfillmentGroup,
}: {
  fulfillmentGroup: FulfillmentGroupData;
}) {
  const team = useContext(TeamContext);
  const showCustomAttributes =
    !!team?.settings?.outboundLabels?.degroupItemsByCustomAttributes;
  const hideUnderscoreCustomAttributes =
    !!team?.settings?.outboundLabels?.hideUnderscoreCustomAttributes;
  const items = useMemo(() => {
    return groupFulfillmentOrderLineItems(fulfillmentGroup.items, {
      degroupByCustomAttributes: showCustomAttributes,
    });
  }, [fulfillmentGroup, showCustomAttributes]);

  return (
    <Flex dir="column" gap="none" height="fit-content" w="full">
      <ItemTableHeader showAmountPaid />
      <Flex dir="column" flex="1" gap="none" w="full">
        {items.map((item, index) => {
          const unitDiscountedPrice = Number(item.unitDiscountedPrice.amount);
          const amountPaidMoney = toMoney({
            amount: Number.isFinite(unitDiscountedPrice)
              ? unitDiscountedPrice * item.quantity
              : null,
            currency: item.unitDiscountedPrice.currency,
          });

          return (
            <Flex
              borderBottomWidth={index !== items.length - 1 ? "1px" : "none"}
              borderColor="secondary"
              borderStyle="solid"
              dir="column"
              gap="none"
              key={index}
              w="full"
            >
              <ProductListRow
                amountPaidMoney={amountPaidMoney}
                customAttributes={
                  showCustomAttributes
                    ? hideUnderscoreCustomAttributes
                      ? item.customAttributes?.filter(
                          (attr) => !attr.key.startsWith("_"),
                        )
                      : item.customAttributes
                    : undefined
                }
                imageTooltipPlacement="top"
                imageUrl={item.imageUrl}
                price={item.unitDiscountedPrice.amount}
                priceMoney={toMoney(item.unitDiscountedPrice)}
                quantity={item.quantity}
                sku={item.sku}
                title={item.productTitle || item.title}
                variantTitle={item.variantTitle}
              />
              {item.lots?.length ? (
                <Flex pb="lg" px="xl" w="full">
                  <LineItemLots lots={item.lots} />
                </Flex>
              ) : null}
            </Flex>
          );
        })}
      </Flex>
    </Flex>
  );
});
